/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Text, Box, Flex, useDisclosure, SimpleGrid } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { User } from 'phosphor-react';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import type { IData, IPagination, ISelect } from '../../../../types/shared';
import { useCompanies } from '../../../../hooks/useCompanies';
import type { ICompany, IPartner } from '../../../../hooks/useCompanies';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { PageBox } from '../../../../components/pageBox';
import { PartnerSummaryCards } from './components/PartnerSummaryCards';
import { PartnerDetailModal } from './components/PartnerDetailModal';
import { PartnerTable } from './components/PartnerTable';
import { PartnerCard } from './components/PartnerCard';
import { PartnerFilters } from './components/PartnerFilters';

moment.locale('pt-br');

export function ModulesHomeSupervisorPartinerPage() {
  const { getPartnerCompany, getQuebrasTaxRegimeTotais } = useCompanies();
  const {
    isOpen: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure();
  const [selectedPartner, setSelectedPartner] = useState<IPartner | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [allPartners, setAllPartners] = useState<IPartner[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: '50',
      search: '',
      companyId: '',
      screen_data: 'faturamento',
      date_start: moment().startOf('year').format('YYYY-MM-DD'),
      date_end: moment().endOf('year').format('YYYY-MM-DD'),
    }),
  );

  // Obter o ano atual do filtro
  const currentFilterYear = moment(
    filters.get('date_start') ?? moment().format('YYYY-MM-DD'),
  ).year();

  // Funções para navegar entre anos
  const handlePrevYear = () => {
    const prevYear = currentFilterYear - 1;
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: moment().year(prevYear).startOf('year').format('YYYY-MM-DD'),
        date_end: moment().year(prevYear).endOf('year').format('YYYY-MM-DD'),
      }),
    );
  };

  const handleNextYear = () => {
    const nextYear = currentFilterYear + 1;
    if (nextYear <= moment().year()) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: moment().year(nextYear).startOf('year').format('YYYY-MM-DD'),
          date_end: moment().year(nextYear).endOf('year').format('YYYY-MM-DD'),
        }),
      );
    }
  };

  const handleSelectYear = (year: number) => {
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: moment().year(year).startOf('year').format('YYYY-MM-DD'),
        date_end: moment().year(year).endOf('year').format('YYYY-MM-DD'),
      }),
    );
  };

  // Função para aplicar os filtros avançados quando clica no botão "Filtrar"
  const handleApplyFilters = (values: {
    companies?: ICompany;
    cidade?: ISelect<string>;
    uf?: ISelect<string>;
    grupo_de_empresas?: ISelect<string>;
    tags?: ISelect<string>;
    comercial?: ISelect<string>;
    contabil?: ISelect<string>;
    financeiro?: ISelect<string>;
    contas_a_pagar?: ISelect<string>;
    encerramento_anual?: ISelect<string>;
    fiscal?: ISelect<string>;
    imposto_de_renda?: ISelect<string>;
    integracao_contabil?: ISelect<string>;
    integracao_fiscal?: ISelect<string>;
    pessoal?: ISelect<string>;
    relacionamento_com_cliente?: ISelect<string>;
    societario?: ISelect<string>;
  }) => {
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    newFilters.set('screen_data', 'faturamento');

    // Mantém datas se existirem
    const dateStart = filters.get('date_start');
    const dateEnd = filters.get('date_end');
    if (dateStart) newFilters.set('date_start', dateStart);
    if (dateEnd) newFilters.set('date_end', dateEnd);

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  const handleSearchChange = (search: string, searchMode?: 'Nome' | 'CPF' | 'CNPJ') => {
    const newFilters = new URLSearchParams(filters);
    if (search) {
      if (searchMode === 'CPF') {
        newFilters.set('cpf', search.replace(/\D/g, ''));
        newFilters.delete('search');
        newFilters.delete('cnpj');
      } else if (searchMode === 'CNPJ') {
        newFilters.set('cnpj', search.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
        newFilters.delete('search');
        newFilters.delete('cpf');
      } else {
        newFilters.set('search', search);
        newFilters.delete('cpf');
        newFilters.delete('cnpj');
      }
    } else {
      newFilters.delete('search');
      newFilters.delete('cpf');
      newFilters.delete('cnpj');
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  };

  // Calcular filtros ativos para indicadores visuais
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.get('id')) count += 1;
    if (filters.get('cidade')) count += 1;
    if (filters.get('uf')) count += 1;
    if (filters.get('grupo_de_empresas')) count += 1;
    if (filters.get('tags')) count += 1;
    if (filters.get('comercial')) count += 1;
    if (filters.get('contabil')) count += 1;
    if (filters.get('financeiro')) count += 1;
    if (filters.get('contas_a_pagar')) count += 1;
    if (filters.get('encerramento_anual')) count += 1;
    if (filters.get('fiscal')) count += 1;
    if (filters.get('imposto_de_renda')) count += 1;
    if (filters.get('integracao_contabil')) count += 1;
    if (filters.get('integracao_fiscal')) count += 1;
    if (filters.get('pessoal')) count += 1;
    if (filters.get('relacionamento_com_cliente')) count += 1;
    if (filters.get('societario')) count += 1;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    const clearedFilters = createSearchParams({
      page: '1',
      size: '50',
      search: filters.get('search') || '',
      screen_data: 'faturamento',
    });

    // Mantém datas se existirem
    const dateStart = filters.get('date_start');
    const dateEnd = filters.get('date_end');
    if (dateStart) clearedFilters.set('date_start', dateStart);
    if (dateEnd) clearedFilters.set('date_end', dateEnd);

    setFilters(clearedFilters);
  };

  // Criar filtros sem page para a query
  const filtersWithoutPage = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    delete baseFilters.page;
    return {
      ...baseFilters,
      page: currentPage,
      size: '50',
    };
  }, [filters, currentPage]);

  const { data: dataPartnerList, isFetching: isFetchingPartnerList } = useQuery<IData<IPartner[]>>({
    queryKey: ['partnerList', cleanFilters(filtersWithoutPage)],
    queryFn: getPartnerCompany,
  });

  const { items: partnerList, ...pagination } = dataPartnerList?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  // Chave para detectar mudança de filtros (exceto page)
  const filterKey = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    delete baseFilters.page;
    return JSON.stringify(baseFilters);
  }, [filters]);

  // Ref para rastrear o filterKey anterior e detectar mudanças
  const lastFilterKeyRef = useRef<string>(filterKey);

  // Função para atualizar allPartners baseado nos dados e contexto
  const updatePartners = useCallback(
    (data: IPartner[], page: number, total: number, isFilterChange: boolean) => {
      if (isFilterChange || page === 1) {
        setAllPartners(data);
      } else if (data.length > 0) {
        setAllPartners(prev => [...prev, ...data]);
      }
      const totalPages = Math.ceil(total / 50);
      setHasMore(page < totalPages);
    },
    [],
  );

  // Efeito unificado para lidar com mudanças de filtro e acumulação de dados
  useEffect(() => {
    const isFilterChange = lastFilterKeyRef.current !== filterKey;

    if (isFilterChange) {
      lastFilterKeyRef.current = filterKey;
      setCurrentPage(1);

      // Se já temos dados no cache, usar diretamente
      if (partnerList) {
        updatePartners(partnerList, 1, pagination.total || 0, true);
      } else {
        setAllPartners([]);
        setHasMore(true);
      }
      return;
    }

    // Caso normal: acumular dados quando não é mudança de filtro
    if (partnerList && partnerList.length > 0) {
      updatePartners(partnerList, currentPage, pagination.total || 0, false);
    } else if (partnerList && partnerList.length === 0 && currentPage === 1) {
      setAllPartners([]);
      setHasMore(false);
    }
  }, [filterKey, partnerList, currentPage, pagination.total, updatePartners]);

  // Intersection Observer para infinite scroll (funciona em ambos os modos)
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || isFetchingPartnerList) {
      return () => {
        // Cleanup vazio quando não há observer
      };
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isFetchingPartnerList) {
          setCurrentPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isFetchingPartnerList]);

  // As contagens de empresas já vêm do backend na resposta de sócios
  // Não é mais necessário fazer requisições adicionais
  const enrichedPartnerList = useMemo(() => {
    return allPartners.map(partner => ({
      ...partner,
      empresas_simples: partner.empresas_simples ?? 0,
      empresas_presumido: partner.empresas_presumido ?? 0,
    }));
  }, [allPartners]);

  // Buscar totais de empresas usando endpoint de totais (mais eficiente)
  const filtersForTotals = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    // Remove campos que não são necessários para o endpoint de totais
    delete baseFilters.page;
    delete baseFilters.size;
    delete baseFilters.screen_data;
    return baseFilters;
  }, [filters]);

  const { data: dataTotals } = useQuery({
    queryKey: ['taxRegimeTotals', filtersForTotals],
    queryFn: getQuebrasTaxRegimeTotais,
    enabled: true,
  });

  // Calcular totais para os cards de resumo
  const totals = useMemo(() => {
    const totalPartners = pagination.total ?? 0;
    const totalFaturamento = allPartners.reduce(
      (acc, partner) => acc + (partner.faturamento_acumulado || 0),
      0,
    );
    const totalEmpresasSimples = dataTotals?.simples_nacional || 0;
    const totalEmpresasPresumido = dataTotals?.lucro_presumido || 0;
    const totalEmpresas = totalEmpresasSimples + totalEmpresasPresumido;

    return {
      totalPartners,
      totalFaturamento,
      totalEmpresas,
      totalEmpresasSimples,
      totalEmpresasPresumido,
    };
  }, [allPartners, pagination.total, dataTotals]);

  // Handler para abrir modal ao clicar em um sócio
  const handleRowClick = (partner: IPartner) => {
    setSelectedPartner(partner);
    onDetailModalOpen();
  };

  return (
    <PageBox>
      {/* Header com título */}
      <div className="flex content-start pb-5 md:w-1/3 lg:content-center">
        <ReturnButton previousPage="modulesHome/supervisor" />

        <div className="h-full">
          <div className="flex items-center justify-center rounded-full bg-[#CFD8DC] p-3">
            <User color="#37474F" className="size-8" />
          </div>
        </div>
        <div className="flex w-full flex-col justify-center gap-1 pl-2">
          <Text fontWeight="bold" fontSize="16px">
            FATURAMENTO DOS SÓCIOS
          </Text>
          <Text fontSize="12px" color="gray.500">
            Visualize os sócios e suas empresas
          </Text>
        </div>
      </div>

      {/* Cards de Resumo */}
      <PartnerSummaryCards
        totalPartners={totals.totalPartners}
        totalEmpresas={totals.totalEmpresas}
        totalEmpresasSimples={totals.totalEmpresasSimples}
        totalEmpresasPresumido={totals.totalEmpresasPresumido}
        isLoading={isFetchingPartnerList}
      />

      {/* Filtros */}
      <PartnerFilters
        filters={filters}
        currentYear={currentFilterYear}
        activeFiltersCount={activeFiltersCount}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onPrevYear={handlePrevYear}
        onNextYear={handleNextYear}
        onSelectYear={handleSelectYear}
        onSearchChange={handleSearchChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      {/* Lista de Sócios */}
      {viewMode === 'table' ? (
        <Box>
          <PartnerTable
            partnerList={enrichedPartnerList}
            isLoading={isFetchingPartnerList && allPartners.length === 0}
            onRowClick={handleRowClick}
          />
          {/* Elemento para trigger do infinite scroll no modo tabela */}
          {hasMore && (
            <Box ref={loadMoreRef} py={4} display="flex" justifyContent="center">
              {isFetchingPartnerList && <Spinner size="lg" color="blue.500" />}
            </Box>
          )}
        </Box>
      ) : isFetchingPartnerList || allPartners.length === 0 ? (
        isFetchingPartnerList ? (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={4} py={8}>
            <Spinner size="lg" color="primary" />
            <Text fontSize="xl" fontWeight="semibold">
              Carregando...
            </Text>
          </Flex>
        ) : (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={4} py={8}>
            <Text fontSize="lg" color="gray.500">
              Nenhum item encontrado
            </Text>
          </Flex>
        )
      ) : (
        <Box>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {enrichedPartnerList?.map(item => (
              <PartnerCard
                key={item.socio_id}
                partner={item}
                onClick={() => handleRowClick(item)}
              />
            ))}
          </SimpleGrid>

          {/* Elemento para trigger do infinite scroll */}
          {hasMore && (
            <Box ref={loadMoreRef} py={4} display="flex" justifyContent="center">
              {isFetchingPartnerList && <Spinner size="lg" color="blue.500" />}
            </Box>
          )}
        </Box>
      )}

      {/* Modal de Detalhes com Gráficos */}
      <PartnerDetailModal
        isOpen={isDetailModalOpen}
        onClose={onDetailModalClose}
        partner={selectedPartner}
        currentYear={currentFilterYear}
      />
    </PageBox>
  );
}
